import useAutoComplete from '@app/lui2/AutoComplete/useAutoComplete';
import FloatingList from '@app/lui2/FloatingList';
import FloatingPortal from '@app/lui2/FloatingPortal';
import Input from '@app/lui2/Input';
import ListItem from '@app/lui2/ListItem';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

interface Props<T> {
  title: string;
  placeholder: string;
  searchOptions: (query: string) => Promise<T[]>;
  getOptionKey: (option: T) => string | number;
  renderOption: (option: T) => ReactNode;
  onSelect: (option: T) => void;
  excludeKeys?: ReadonlySet<string | number>;
  emptyMessage?: string;
}

const Flex = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const Label = styled.div`
  font-weight: 600;
  font-size: 0.75rem;
  line-height: 1rem;
  letter-spacing: 0.02em;
`;

const LinkSelector = <T,>({
  title,
  placeholder,
  searchOptions,
  getOptionKey,
  renderOption,
  onSelect,
  excludeKeys,
  emptyMessage = 'No results',
}: Props<T>) => {
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const {
    getFloatingProps,
    getInputProps,
    getItemProps,
    open,
    search,
    resetSearch,
    setOpen,
  } = useAutoComplete<T>(
    (_index, value) => {
      if (value == null) {
        return;
      }
      onSelect(value);
      resetSearch('');
      setOpen(false);
    },
    {
      openOnFocus: true,
      stayOpenOnEmptySearch: true,
      skipFocusOnOptionClick: true,
    }
  );

  const loadOptions = useCallback(
    async (query: string, requestId: number) => {
      setLoading(true);
      try {
        const results = await searchOptions(query);
        if (requestIdRef.current === requestId) {
          setOptions(results);
        }
      } catch {
        if (requestIdRef.current === requestId) {
          setOptions([]);
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [searchOptions]
  );

  useEffect(() => {
    if (!open) {
      requestIdRef.current += 1;
      setLoading(false);
      return;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    void loadOptions(search, requestId);
    return () => {
      if (requestIdRef.current === requestId) {
        requestIdRef.current += 1;
      }
    };
  }, [open, search, loadOptions]);

  const visibleOptions = useMemo(
    () => options.filter((option) => !excludeKeys?.has(getOptionKey(option))),
    [options, excludeKeys, getOptionKey]
  );

  const inputProps = getInputProps();

  return (
    <>
      <Flex>
        <Label>{title}</Label>
        <Input
          {...inputProps}
          placeholder={placeholder}
          beforeFaIcon="search"
        />
      </Flex>
      <FloatingPortal>
        {open && (
          <FloatingList {...getFloatingProps()}>
            {loading && visibleOptions.length === 0 && (
              <ListItem subHeading>Searching…</ListItem>
            )}
            {!loading && visibleOptions.length === 0 && (
              <ListItem subHeading>{emptyMessage}</ListItem>
            )}
            {visibleOptions.map((option, index) => (
              <ListItem
                key={getOptionKey(option)}
                {...getItemProps(index, option)}
              >
                {renderOption(option)}
              </ListItem>
            ))}
          </FloatingList>
        )}
      </FloatingPortal>
    </>
  );
};

export default LinkSelector;
